// Multi-pedigree-tabs — open-set persistence.
//
// Persists the OPEN SET (which pedigrees are open, their tab order, mode, and
// which is active/preview) to sessionStorage so a page refresh in the same
// browser tab restores the tabs. Deliberately sessionStorage, not localStorage:
// window.localStorage.clear() is called elsewhere (e.g. Questionnaire) and would
// wipe it, and the open set is naturally per-browser-tab.
//
// Stores the durable fields — NOT the transient lock state. The
// per-tab viewState (selected node / expanded section / camera) IS persisted so a
// refresh can restore where each tab was left (FU9).
//
// OWNER SCOPING (TGCLOUD-1618). The snapshot records the id of the user who owns
// it, and a restore is refused unless it matches the user asking. This existed as
// a single unscoped key, and sessionStorage is per browser TAB — cleared only when
// the tab closes, so it survives logout → login in the same tab. The next user
// therefore inherited the previous user's open pedigrees, which are usually
// another tenant's families: every restored tab 403s, and (before the Phase 2 fix)
// each failure navigated the whole app back to the family list, so no pedigree
// could be opened at all.
//
// The owner check is what makes this safe; `clearWorkspace` on logout is the tidy-
// up. Fail-closed: a snapshot with no owner, or one written before this change, is
// discarded rather than trusted.

import { SESSION_KIND, subjectKeyFor } from './workspaceReducer';

export const WORKSPACE_STORAGE_KEY = 'tgc:mpt:workspace:v1';

// Reduce live state to the minimal durable snapshot, stamped with its owner.
export function serializeWorkspace(state, owner) {
  if (!state || !Array.isArray(state.sessions)) return null;
  return {
    v: 1,
    owner: owner == null ? null : String(owner),
    sessions: state.sessions.map((s) => ({
      sessionId: s.sessionId,
      // TGCLOUD-1669 — a session is no longer always a family, so what makes it
      // unique has to survive a reload too. Without `kind` and `memberId` a
      // person tab would come back as a pedigree tab pointing at the family the
      // person belongs to.
      kind: s.kind || SESSION_KIND.pedigree,
      familyId: s.familyId,
      memberId: s.memberId || null,
      title: s.title,
      mode: s.mode,
      viewState: s.viewState || null
    })),
    activeSessionId: state.activeSessionId,
    previewSessionId: state.previewSessionId
  };
}

// Rebuild a RESTORE payload (full session shape with transient fields reset), but
// only for the user the snapshot belongs to.
export function deserializeWorkspace(raw, owner) {
  if (!raw || !Array.isArray(raw.sessions)) return null;
  // No owner on either side is not "a match" — it is a refusal. An unowned
  // snapshot is either pre-TGCLOUD-1618 or was written with no user in context,
  // and in both cases we cannot show it was this user's.
  if (!owner || !raw.owner || String(raw.owner) !== String(owner)) return null;
  const sessions = raw.sessions
    // Migrating rather than version-bumping: a snapshot written before person
    // tabs existed has no `kind`, and every session in it is a pedigree. Reading
    // it as such costs nothing and means nobody loses their open tabs to this
    // change. A session is kept if it still has an identifiable subject.
    .map((s) => {
      if (!s || !s.sessionId) return null;
      const kind =
        s.kind === SESSION_KIND.person
          ? SESSION_KIND.person
          : SESSION_KIND.pedigree;
      const subjectKey = subjectKeyFor({
        kind,
        familyId: s.familyId,
        memberId: s.memberId
      });
      return subjectKey ? { ...s, kind, subjectKey } : null;
    })
    .filter(Boolean)
    .map((s) => ({
      sessionId: s.sessionId,
      kind: s.kind,
      familyId: s.familyId || null,
      memberId: s.memberId || null,
      subjectKey: s.subjectKey,
      title: s.title || s.memberId || s.familyId,
      probandName: '',
      mode: s.mode === 'pinned' ? 'pinned' : 'preview',
      lock: 'none',
      inaccessible: false,
      releaseNonce: 0,
      lastActiveAt: 0,
      // Restore the per-tab view state (selected node / section / camera) so a
      // refresh returns each tab to where it was left.
      viewState: s.viewState || null
    }));
  if (sessions.length === 0) return null;
  const ids = new Set(sessions.map((s) => s.sessionId));
  return {
    sessions,
    activeSessionId: ids.has(raw.activeSessionId) ? raw.activeSessionId : null,
    previewSessionId: ids.has(raw.previewSessionId)
      ? raw.previewSessionId
      : null
  };
}

function safeSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function loadWorkspace(owner, storage = safeSessionStorage()) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(WORKSPACE_STORAGE_KEY);
    if (!raw) return null;
    return deserializeWorkspace(JSON.parse(raw), owner);
  } catch {
    return null;
  }
}

export function saveWorkspace(state, owner, storage = safeSessionStorage()) {
  if (!storage) return;
  // No owner: write nothing rather than a snapshot that can never be restored.
  // Deliberately not a clear either — a transient null user must not destroy a
  // good snapshot. Logout clears it explicitly.
  if (!owner) return;
  try {
    const snapshot = serializeWorkspace(state, owner);
    if (!snapshot || snapshot.sessions.length === 0) {
      storage.removeItem(WORKSPACE_STORAGE_KEY);
      return;
    }
    storage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* quota / disabled storage — persistence is best-effort */
  }
}

// Drop the open set entirely. Called on logout so the next user in this browser
// tab starts clean, and so a snapshot naming a signed-out user does not sit in
// storage waiting to be offered back.
export function clearWorkspace(storage = safeSessionStorage()) {
  if (!storage) return;
  try {
    storage.removeItem(WORKSPACE_STORAGE_KEY);
  } catch {
    /* disabled storage — best-effort */
  }
}
